"use client";

import { useEffect, useRef, useState } from "react";
import {
  motion,
  useMotionTemplate,
  useReducedMotion,
  useScroll,
  useTransform,
} from "framer-motion";
import { Breadcrumbs } from "@/components/shared/breadcrumbs";
import { cn } from "@/lib/utils";
import { Sky } from "./sky-ui";

/**
 * Hero podstron w stylu „Sky": niebo na pełnej szerokości pod pływającym menu
 * (strona musi renderować `SiteHeader overSky`), przy scrollu kurczy się w kartę.
 */
export function SkyPageHero({
  eyebrow,
  title,
  lead,
  breadcrumbs,
  children,
  after,
  align = "center",
  size = "default",
}: {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  lead?: React.ReactNode;
  breadcrumbs?: { label: string; href: string }[];
  /** Akcje / meta pod leadem (przyciski, plakietki). */
  children?: React.ReactNode;
  /** Treść pod tekstem, nadal na niebie (np. zrzut ekranu, formularz). */
  after?: React.ReactNode;
  align?: "center" | "left";
  size?: "default" | "compact";
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 767px)");
    const on = () => setIsMobile(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  const endInset = reduce ? 0 : isMobile ? 10 : 20;
  const endRadius = reduce ? 0 : isMobile ? 28 : 44;
  const inset = useTransform(scrollYProgress, [0, 0.6], [0, endInset]);
  const radius = useTransform(scrollYProgress, [0, 0.6], [0, endRadius]);
  const clipPath = useMotionTemplate`inset(0px ${inset}px 0px ${inset}px round 0px 0px ${radius}px ${radius}px)`;
  const center = align === "center";

  return (
    <section ref={ref} className="-mt-16">
      <motion.div
        style={{ clipPath }}
        className={cn(
          "relative overflow-hidden",
          size === "compact" ? "pb-14 pt-28 md:pb-20 md:pt-36" : "pb-16 pt-32 md:pb-24 md:pt-40"
        )}
      >
        <Sky variant="page" />
        {breadcrumbs && <Breadcrumbs items={breadcrumbs} onSky className="relative mb-8 max-w-6xl" />}
        <div className={cn("relative mx-auto max-w-6xl px-4", center ? "text-center" : "text-left")}>
          {eyebrow && (
            <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 ring-1 ring-white/30 backdrop-blur">
              <span className="h-1.5 w-1.5 rounded-full bg-white" />
              <span className="text-xs font-medium uppercase tracking-wide text-white">{eyebrow}</span>
            </div>
          )}
          <motion.h1
            initial={reduce ? false : { y: 16 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className={cn(
              "text-balance font-bold leading-[1.08] tracking-tight text-white",
              eyebrow && "mt-6",
              center && "mx-auto",
              size === "compact" ? "max-w-4xl text-3xl sm:text-4xl md:text-5xl" : "max-w-4xl text-4xl sm:text-5xl md:text-6xl"
            )}
          >
            {title}
          </motion.h1>
          {lead && (
            <p
              className={cn(
                "mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-white/90 md:text-xl",
                center && "mx-auto"
              )}
            >
              {lead}
            </p>
          )}
          {children && <div className="mt-8">{children}</div>}
        </div>
        {after && <div className="relative mx-auto mt-12 max-w-6xl px-4 md:mt-16">{after}</div>}
      </motion.div>
    </section>
  );
}
